import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
   ChevronLeft, Download, FileText,
   ExternalLink, Info, BookOpen, Clock,
   Eye, Lock, Loader2
} from "lucide-react";
import { cn, getFileUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useGetNotesQuery } from "@/redux/slices/noteApi";
import { format } from "date-fns";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SecurePdfViewer } from "@/components/SecurePdfViewer";

export default function NoteDetailsPage() {
   const { id } = useParams();
   const navigate = useNavigate();
   const [viewType, setViewType] = useState<"demo" | "full">("demo");
   const { data: notesData, isLoading } = useGetNotesQuery();
   const note = notesData?.data?.find(n => n.id === id);

   if (isLoading) return (
      <div className="flex h-screen flex-col items-center justify-center bg-zinc-950 text-white gap-4">
         <Loader2 className="h-8 w-8 animate-spin text-primary" />
         <p className="text-xs font-black uppercase tracking-[0.3em] animate-pulse">Initializing Secure Environment</p>
      </div>
   );

   if (!note) return (
      <div className="p-20 text-center flex flex-col items-center gap-6 bg-background min-h-screen justify-center">
         <div className="h-20 w-20 rounded-3xl bg-muted flex items-center justify-center shadow-inner">
            <Info className="h-10 w-10 text-muted-foreground" />
         </div>
         <div className="space-y-2">
            <p className="font-black text-2xl uppercase tracking-tighter">Document Expired or Missing</p>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">The requested study material could not be located in our secure database.</p>
         </div>
         <Button onClick={() => navigate("/notes")} className="rounded-xl px-8 h-12 font-bold uppercase tracking-widest text-[10px]">Return to Library</Button>
      </div>
   );

   const currentPdfPath = viewType === "demo" ? note.demoUrl : note.fullUrl;
   const resolvedPdfUrl = getFileUrl(currentPdfPath);

   return (
      <div className="flex flex-col h-screen bg-muted/10 overflow-hidden">
         {/* Premium Navigation Header */}
         <div className="flex shrink-0 items-center justify-between border-b bg-card px-8 py-4 shadow-xl z-30">
            <div className="flex items-center gap-6">
               <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-2xl hover:bg-primary/10 hover:text-primary transition-all h-11 w-11 border bg-muted/20">
                  <ChevronLeft className="h-5 w-5" />
               </Button>
               <div className="hidden md:block">
                  <h1 className="text-sm font-black tracking-tight line-clamp-1 uppercase mb-0.5">{note.title}</h1>
                  <div className="flex items-center gap-2">
                     <Badge variant="outline" className="text-[9px] font-black uppercase tracking-widest bg-primary/5 text-primary border-primary/20 px-2 py-0">
                        {note.subject?.name}
                     </Badge>
                     <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">{note.chapter?.name}</span>
                  </div>
               </div>
            </div>

            <div className="flex items-center gap-6">
               <Tabs value={viewType} onValueChange={(v) => setViewType(v as "demo" | "full")} className="w-[240px]">
                  <TabsList className="grid w-full grid-cols-2 rounded-2xl h-11 p-1 bg-muted/50 border shadow-inner">
                     <TabsTrigger value="demo" className="text-[10px] font-black uppercase tracking-widest gap-2 data-[state=active]:bg-white data-[state=active]:shadow-lg rounded-xl transition-all">
                        <Eye className="h-3.5 w-3.5" /> Preview
                     </TabsTrigger>
                     <TabsTrigger value="full" className="text-[10px] font-black uppercase tracking-widest gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-lg rounded-xl transition-all">
                        <Lock className="h-3.5 w-3.5" /> Full
                     </TabsTrigger>
                  </TabsList>
               </Tabs>

               <Separator orientation="vertical" className="h-10 hidden sm:block opacity-50" />

               <Button size="sm" variant="default" className="gap-2 shadow-2xl shadow-primary/30 h-11 px-6 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em]" asChild>
                  <a href={resolvedPdfUrl} download target="_blank">
                     <Download className="h-4 w-4" /> Download
                  </a>
               </Button>
            </div>
         </div>

         <div className="flex flex-1 overflow-hidden relative">
            {/* PDF Viewer Area */}
            <div className="flex-1 bg-zinc-950 relative overflow-hidden">
               {resolvedPdfUrl ? (
                  <SecurePdfViewer
                     url={resolvedPdfUrl}
                     title={note.title}
                     viewType={viewType}
                  />
               ) : (
                  <div className="flex h-full flex-col items-center justify-center text-white/20 gap-6">
                     <div className="h-24 w-24 rounded-full border-4 border-dashed border-white/10 flex items-center justify-center">
                        <FileText className="h-10 w-10 opacity-20" />
                     </div>
                     <p className="text-[10px] font-black uppercase tracking-[0.4em]">Resource Link Broken</p>
                  </div>
               )}

               <div className="absolute top-8 right-8 flex flex-col gap-3 z-20">
                  <Button size="icon" variant="secondary" className="h-12 w-12 rounded-2xl bg-white/5 text-white hover:bg-white/10 border-white/5 backdrop-blur-3xl shadow-2xl transition-all hover:scale-105 active:scale-95" asChild>
                     <a href={resolvedPdfUrl} target="_blank"><ExternalLink className="h-5 w-5" /></a>
                  </Button>
               </div>
            </div>

            {/* Intelligence Sidebar */}
            <div className="w-[350px] border-l bg-card hidden xl:flex flex-col shadow-2xl z-30 overflow-hidden">
               <div className="p-10 space-y-10 overflow-y-auto flex-1 custom-scrollbar">
                  <div className="space-y-4">
                     <h3 className="text-[10px] font-black flex items-center gap-2 text-primary uppercase tracking-[0.3em]">
                        <Info className="h-4 w-4" /> Document Synopsis
                     </h3>
                     <p className="text-[13px] text-foreground font-medium leading-[1.8] tracking-tight opacity-80">
                        {note.description || "No abstract available for this resource. This document contains primary research and curated notes for the medical curriculum."}
                     </p>
                  </div>

                  <Separator className="opacity-10" />

                  <div className="space-y-6">
                     <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                           <BookOpen className="h-3.5 w-3.5 opacity-40" /> Academic Subject
                        </span>
                        <span className="text-sm font-black text-primary uppercase tracking-tight ml-5.5">{note.subject?.name}</span>
                     </div>

                     <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                           <FileText className="h-3.5 w-3.5 opacity-40" /> Chapter Reference
                        </span>
                        <span className="text-sm font-black uppercase tracking-tight ml-5.5">{note.chapter?.name}</span>
                     </div>

                     <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                           <Clock className="h-3.5 w-3.5 opacity-40" /> Publication Date
                        </span>
                        <span className="text-sm font-black uppercase tracking-tight ml-5.5">{format(new Date(note.createdAt), "dd MMMM yyyy")}</span>
                     </div>
                  </div>
               </div>

               <div className="mt-auto p-10 border-t bg-muted/5 backdrop-blur-sm">
                  <Button variant="outline" className="w-full h-14 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] shadow-sm hover:bg-background transition-all border bg-card" onClick={() => navigate("/notes")}>
                     Close Environment
                  </Button>
               </div>
            </div>
         </div>
      </div>
   );
}
